import React, { useState, useEffect } from "react";
import { Box, Typography, makeStyles, Chip, CircularProgress } from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppDialog from "components/Dialog/AppDialog";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import PreviewDialog from "components/Dialog/PreviewDialog";
import InvestigationActionForm from "./form/InvestigationActionForm";
import CustomDatePicker from "components/Input/CustomDatePicker";
import { showToast } from "store/slices/toast";
import { ACTION_SOURCE, ACTION_STATUS, ACTION_OWNER } from "config/AppConfig";
import {
  fetchPageAction,
  createAction,
  updateActionStatus,
  getActionList,
} from "store/slices/action";
import { fetchCompanyUserListPaging, getCompanyUserListPaging } from "store/slices/companyUser";
import { fetchWorkerListPaging, getWorkerList } from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import {
  fetchInvestigationDetail,
  updateInvestigationStepStatus,
  fetchInvestigationEvidence,
  uploadInvestigationEvidence,
  deleteInvestigationEvidence,
  getInvestigationDetail,
  getInvestigationEvidenceList,
} from "store/slices/investigation";

const STEP_CONFIG = [
  { key: "RECORD", number: 1, label: "Record" },
  { key: "ROOT_CAUSE", number: 2, label: "Root cause" },
  { key: "CORRECTIVE_ACTION", number: 3, label: "Corrective action" },
  { key: "EVIDENCE", number: 4, label: "Evidence upload" },
  { key: "DIRECTOR_SIGN_OFF", number: 5, label: "Director sign-off" },
];

const STEP_STATUS_LABEL = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  COMPLETE: "Complete",
};

const ACTION_STATUS_LABEL = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  OVERDUE: "Overdue",
};

const formatActionDueDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const useStyles = makeStyles(() => ({
  container: {
    backgroundColor: "#F5F5F3",
    minHeight: "100vh",
  },
  navBreadcrumb: {
    padding: "16px 24px",
    borderBottom: "0.5px solid #e5e5e2",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff",
  },
  breadcrumbLink: {
    fontSize: "13px",
    color: "#1D9E75",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
  },
  breadcrumbDivider: {
    color: "#aaa",
    fontSize: "13px",
  },
  breadcrumbText: {
    fontSize: "13px",
    color: "#5f5e5a",
  },
  pageHeader: {
    padding: "24px 24px 0",
  },
  pageSubtitle: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    letterSpacing: "0.04em",
    marginBottom: "4px",
  },
  pageTitle: {
    fontSize: "20px",
    fontWeight: 500,
    marginBottom: "8px",
    color: "#1a1a18",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  metaItem: {
    fontSize: "13px",
    color: "#5f5e5a",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pageBody: {
    padding: "20px 24px",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    border: "0.5px solid #e5e5e2",
    padding: "20px",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#1a1a18",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "16px",
  },
  metaLabel: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    letterSpacing: "0.04em",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  workflow: {
    display: "flex",
    alignItems: "flex-start",
  },
  stepCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  stepCircle: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 500,
    zIndex: 1,
    position: "relative",
  },
  stepDone: {
    background: "#1D9E75",
    color: "#fff",
  },
  stepActive: {
    background: "#fff",
    border: "2px solid #1D9E75",
    color: "#1D9E75",
  },
  stepPending: {
    background: "#fff",
    border: "2px solid #c5c4bc",
    color: "#888780",
  },
  stepLine: {
    height: "2px",
    width: "calc(100% - 32px)",
    position: "absolute",
    top: "16px",
    left: "calc(50% + 16px)",
    zIndex: 0,
  },
  stepLabel: {
    fontSize: "11px",
    marginTop: "6px",
    textAlign: "center",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    tableLayout: "fixed",
  },
  th: {
    textAlign: "left",
    padding: "8px 0",
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    borderBottom: "0.5px solid #e5e5e2",
  },
  td: {
    padding: "10px 0",
    color: "#1a1a18",
    borderBottom: "0.5px solid #F1EFE8",
  },
  uploadZone: {
    border: "1.5px dashed #E5E5EB",
    borderRadius: "8px",
    padding: "32px",
    textAlign: "center",
  },
  uploadIcon: {
    fontSize: "28px",
    color: "#6B7280",
  },
  uploadText: {
    fontSize: "13px",
    color: "#6B7280",
    marginTop: "8px",
  },
  uploadSubtext: {
    fontSize: "11px",
    color: "#6B7280",
    marginTop: "4px",
  },
  input: {
    width: "100%",
    fontSize: "13px",
    padding: "8px 12px",
    border: "0.5px solid #c5c4bc",
    borderRadius: "8px",
    fontFamily: "inherit",
    background: "#fff",
    color: "#1a1a18",
  },
  badgeRed: {
    background: "#FCEBEB",
    color: "#A32D2D",
  },
  badgeGreen: {
    background: "#EAF3DE",
    color: "#3B6D11",
  },
  badgeAmber: {
    background: "#FAEEDA",
    color: "#854F0B",
  },
  badgeGray: {
    background: "#F1EFE8",
    color: "#5f5e5a",
  },
  severityRed: {
    background: "#FEE2E2",
    color: "#991b1b",
  },
  severityAmber: {
    background: "#FEF3C7",
    color: "#92400E",
  },
  severityBlue: {
    background: "#DBEAFE",
    color: "#1E40AF",
  },
}));

const InvestigationDetail = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const investigation = useSelector(getInvestigationDetail);
  const actions = useSelector(getActionList);
  const evidenceList = useSelector(getInvestigationEvidenceList);
  const userInfo = useSelector(getAuthInfo);
  const companyUsers = useSelector(getCompanyUserListPaging);
  const workers = useSelector(getWorkerList);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openEditActionDialog, setOpenEditActionDialog] = useState(false);
  const [editingAction, setEditingAction] = useState(null);

  const [rootCause, setRootCause] = useState({
    category: "",
    description: "",
    contributingFactors: "",
  });
  const [director, setDirector] = useState({
    name: "",
    notes: "",
    signOffDate: "",
  });
  const [stepDialog, setStepDialog] = useState({ open: false, step: null, status: null });
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [deleteEvidenceTarget, setDeleteEvidenceTarget] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchInvestigationDetail({ id }));
      dispatch(fetchPageAction({ source: ACTION_SOURCE.INVESTIGATION, investigationId: id, page: 1, size: 50 }));
      dispatch(fetchInvestigationEvidence({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (userInfo && userInfo.companyId) {
      dispatch(fetchCompanyUserListPaging({ company_id: userInfo.companyId, page_number: 1, page_size: 1000 }));
      dispatch(fetchWorkerListPaging({ companyId: userInfo.companyId, page: 1, size: 1000 }));
    }
  }, [dispatch, userInfo]);

  const companyUserOptions = (companyUsers || []).map((u) => ({
    value: u.id,
    label: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
  }));

  const workerOptions = (workers || []).map((w) => ({
    value: w.id,
    label: w.name || `${w.firstName || ""} ${w.lastName || ""}`.trim(),
  }));

  useEffect(() => {
    setRootCause({
      category: investigation?.rootCauseCategory || "",
      description: investigation?.rootCauseDescription || "",
      contributingFactors: investigation?.contributingFactor || "",
    });
    setDirector({
      name: investigation?.directorName || "",
      notes: investigation?.directorNotes || "",
      signOffDate: investigation?.directorSignOffDate
        ? investigation.directorSignOffDate.substring(0, 10).split("-").reverse().join("/")
        : "",
    });
  }, [investigation]);

  const stepStatuses = {
    RECORD: investigation?.recordStepStatus || "COMPLETE",
    ROOT_CAUSE: investigation?.rootCauseStepStatus || "OPEN",
    CORRECTIVE_ACTION: investigation?.correctiveActionStepStatus || "OPEN",
    EVIDENCE: investigation?.evidenceStepStatus || "OPEN",
    DIRECTOR_SIGN_OFF: investigation?.directorSignOffStepStatus || "OPEN",
  };

  const overallStatus = (() => {
    const steps = [
      stepStatuses.ROOT_CAUSE,
      stepStatuses.CORRECTIVE_ACTION,
      stepStatuses.EVIDENCE,
      stepStatuses.DIRECTOR_SIGN_OFF,
    ];
    if (steps.every((s) => s === "COMPLETE")) {
      return "COMPLETE";
    }
    if (steps.some((s) => s === "IN_PROGRESS" || s === "COMPLETE")) {
      return "IN_PROGRESS";
    }
    return "OPEN";
  })();

  const getStepCircleClass = (status) => {
    switch (status) {
      case "COMPLETE":
        return classes.stepDone;
      case "IN_PROGRESS":
        return classes.stepActive;
      default:
        return classes.stepPending;
    }
  };

  const getStepBadgeClass = (status) => {
    switch (status) {
      case "COMPLETE":
        return classes.badgeGreen;
      case "IN_PROGRESS":
        return classes.badgeAmber;
      default:
        return classes.badgeGray;
    }
  };

  const getSeverityBadgeClass = (severity) => {
    switch ((severity || "").toUpperCase()) {
      case "LOW":
        return classes.severityBlue;
      case "MODERATE":
      case "SERIOUS":
        return classes.severityAmber;
      case "NOTIFIABLE":
        return classes.severityRed;
      default:
        return classes.badgeGray;
    }
  };

  const getActionStatusClass = (status) => {
    switch (status) {
      case ACTION_STATUS.COMPLETED:
        return classes.badgeGreen;
      case ACTION_STATUS.IN_PROGRESS:
        return classes.badgeAmber;
      case ACTION_STATUS.OVERDUE:
        return classes.badgeRed;
      default:
        return classes.badgeGray;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const validateStepComplete = (step) => {
    switch (step) {
      case "ROOT_CAUSE":
        if (!rootCause.category || !rootCause.description) {
          return "Root cause category and description are required before marking this step Complete.";
        }
        return null;
      case "CORRECTIVE_ACTION": {
        if (!actions || actions.length === 0) {
          return "At least one corrective action is required before marking this step Complete.";
        }
        const allCompleted = actions.every((a) => a.status === ACTION_STATUS.COMPLETED);
        if (!allCompleted) {
          return "All corrective actions must be Completed before marking this step Complete.";
        }
        return null;
      }
      case "EVIDENCE":
        if (!evidenceList || evidenceList.length === 0) {
          return "At least one evidence file is required before marking this step Complete.";
        }
        return null;
      case "DIRECTOR_SIGN_OFF":
        if (!director.name || !director.notes || !director.signOffDate) {
          return "Director name, notes and sign-off date are required before marking this step Complete.";
        }
        return null;
      default:
        return null;
    }
  };

  const handleStepClick = (stepKey) => {
    if (stepKey === "RECORD") return;
    setStepDialog({ open: true, step: stepKey, status: stepStatuses[stepKey] });
  };

  const handleConfirmStepStatus = () => {
    const { step, status } = stepDialog;
    if (status === "COMPLETE") {
      const error = validateStepComplete(step);
      if (error) {
        dispatch(showToast({ type: "error", message: error }));
        return;
      }
    }

    const payload = { id, step, status };
    if (step === "ROOT_CAUSE") {
      payload.rootCauseCategory = rootCause.category;
      payload.rootCauseDescription = rootCause.description;
      payload.contributingFactor = rootCause.contributingFactors;
    }
    if (step === "DIRECTOR_SIGN_OFF") {
      payload.directorName = director.name;
      payload.directorNotes = director.notes;
      payload.directorSignOffDate = director.signOffDate;
    }

    dispatch(updateInvestigationStepStatus(payload));
    setStepDialog({ open: false, step: null, status: null });
  };

  const handleRootCauseFieldChange = (field) => (e) => {
    const value = e.target.value;
    setRootCause((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveRootCause = () => {
    dispatch(updateInvestigationStepStatus({
      id,
      step: "ROOT_CAUSE",
      status: stepStatuses.ROOT_CAUSE === "OPEN" ? "IN_PROGRESS" : stepStatuses.ROOT_CAUSE,
      rootCauseCategory: rootCause.category,
      rootCauseDescription: rootCause.description,
      contributingFactor: rootCause.contributingFactors,
    }));
  };

  const handleDirectorFieldChange = (field) => (e) => {
    const value = e.target.value;
    setDirector((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDirectorSignOff = () => {
    dispatch(updateInvestigationStepStatus({
      id,
      step: "DIRECTOR_SIGN_OFF",
      status: stepStatuses.DIRECTOR_SIGN_OFF === "OPEN" ? "IN_PROGRESS" : stepStatuses.DIRECTOR_SIGN_OFF,
      directorName: director.name,
      directorNotes: director.notes,
      directorSignOffDate: director.signOffDate,
    }));
  };

  const handleAddAction = (actionName, expireDate, owner, assignedToUserId, file) => {
    return dispatch(createAction({
      source: ACTION_SOURCE.INVESTIGATION,
      linkedRecordId: id,
      actionDescription: actionName,
      owner,
      assignedToUserId: (owner === ACTION_OWNER.WORKER || owner === ACTION_OWNER.COMPANY) ? assignedToUserId : null,
      dueDate: expireDate ? `${expireDate} 23:59` : null,
      file,
    })).then(() => {
      dispatch(fetchPageAction({ source: ACTION_SOURCE.INVESTIGATION, investigationId: id, page: 1, size: 50 }));
      setIsModalOpen(false);
    });
  };

  const handleOpenEditAction = (action) => {
    setEditingAction(action);
    setOpenEditActionDialog(true);
  };

  const handleUpdateAction = (actionName, expireDate, owner, assignedToUserId, file, status) => {
    return dispatch(updateActionStatus({
      id: editingAction.id,
      status,
      actionDescription: actionName,
      owner,
      assignedToUserId: (owner === ACTION_OWNER.WORKER || owner === ACTION_OWNER.COMPANY) ? assignedToUserId : null,
      dueDate: expireDate ? `${expireDate} 23:59` : null,
      file,
    })).then(() => {
      dispatch(fetchPageAction({ source: ACTION_SOURCE.INVESTIGATION, investigationId: id, page: 1, size: 50 }));
      setOpenEditActionDialog(false);
    });
  };

  const handleEvidenceUpload = (files) => {
    if (!files || !files.length) return;
    setIsUploadingEvidence(true);
    dispatch(uploadInvestigationEvidence({ id, files })).finally(() => {
      setIsUploadingEvidence(false);
    });
  };

  const handleDeleteEvidence = (evidence) => {
    setDeleteEvidenceTarget(evidence);
  };

  const confirmDeleteEvidence = () => {
    const evidence = deleteEvidenceTarget;
    if (!evidence) return;
    dispatch(deleteInvestigationEvidence({ id, evidenceId: evidence.id })).finally(() => {
      setDeleteEvidenceTarget(null);
    });
  };

  const handleDownloadEvidence = async (evidence) => {
    try {
      const response = await fetch(evidence.fileUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = evidence.fileName || `Evidence-${evidence.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.open(evidence.fileUrl, "_blank");
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.navBreadcrumb}>
        <Box
          className={classes.breadcrumbLink}
          onClick={() => history.push("/admin/investigations")}
        >
          <i className="fas fa-arrow-left" style={{ fontSize: "13px" }}></i>
          Incident register
        </Box>
        <span className={classes.breadcrumbDivider}>/</span>
        <span className={classes.breadcrumbText}>{investigation?.incidentNumber || `INC-${id}`}</span>
        <span className={classes.breadcrumbDivider}>/</span>
        <span style={{ fontSize: "13px", color: "#1a1a18", fontWeight: 500 }}>
          Investigation
        </span>
      </Box>

      <Box className={classes.pageHeader}>
        <Box
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <Box>
            <Typography className={classes.pageSubtitle}>
              {investigation?.incidentNumber || `INC-${id}`} — Investigation
            </Typography>
            <Typography className={classes.pageTitle}>
              {investigation?.title || investigation?.subject || "Investigation"}
            </Typography>
            <Box className={classes.metaRow}>
              <span className={classes.metaItem}>
                <i className="fas fa-user" style={{ fontSize: "13px" }}></i>
                {investigation?.participantName || investigation?.participant || ""}
              </span>
              <span className={classes.metaItem}>
                <i className="fas fa-calendar" style={{ fontSize: "13px" }}></i>
                {investigation?.date ? formatDate(investigation.date) : ""}
              </span>
              <Chip
                size="small"
                label={investigation?.severity || ""}
                className={getSeverityBadgeClass(investigation?.severity)}
                style={{ fontSize: "12px", fontWeight: 600 }}
              />
            </Box>
          </Box>
          <Chip
            size="small"
            label={STEP_STATUS_LABEL[overallStatus]}
            className={getStepBadgeClass(overallStatus)}
            style={{ padding: "5px 14px", fontSize: "12px", fontWeight: '600' }}
          />
        </Box>
      </Box>

      <Box className={classes.pageBody}>
        <Box className={classes.card}>
          <Box
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#5f5e5a",
              marginBottom: "16px",
            }}
          >
            Investigation steps
          </Box>
          <Box className={classes.workflow}>
            {STEP_CONFIG.map((step, index) => {
              const status = stepStatuses[step.key];
              const isEditable = step.key !== "RECORD";
              return (
                <Box
                  key={step.key}
                  className={classes.stepCol}
                  style={{ cursor: isEditable ? "pointer" : "default" }}
                  onClick={() => handleStepClick(step.key)}
                >
                  <Box className={`${classes.stepCircle} ${getStepCircleClass(status)}`}>
                    {status === "COMPLETE" ? (
                      <i className="fas fa-check" style={{ fontSize: "14px" }}></i>
                    ) : (
                      step.number
                    )}
                  </Box>
                  {index < STEP_CONFIG.length - 1 && (
                    <Box
                      className={classes.stepLine}
                      style={{ background: status === "COMPLETE" ? "#1D9E75" : "#e5e5e2" }}
                    ></Box>
                  )}
                  <Typography
                    className={classes.stepLabel}
                    style={{ color: "#1D9E75", fontWeight: 500 }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box className={classes.card}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              <i className="fas fa-search" style={{ color: "#1D9E75" }}></i> Root cause analysis
            </Typography>
            <Chip
              size="small"
              label={STEP_STATUS_LABEL[stepStatuses.ROOT_CAUSE]}
              className={getStepBadgeClass(stepStatuses.ROOT_CAUSE)}
              style={{ fontSize: "12px", fontWeight: 600 }}
            />
          </Box>
          <Box style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <Box>
              <Typography className={classes.metaLabel}>Root cause category</Typography>
              <input
                type="text"
                placeholder="e.g. Staff conduct / practice"
                value={rootCause.category}
                onChange={handleRootCauseFieldChange("category")}
                className={classes.input}
              />
            </Box>
            <Box>
              <Typography className={classes.metaLabel}>Root cause description</Typography>
              <textarea
                placeholder="Describe what happened and why"
                rows="3"
                value={rootCause.description}
                onChange={handleRootCauseFieldChange("description")}
                className={classes.input}
                style={{ resize: "vertical" }}
              />
            </Box>
            <Box>
              <Typography className={classes.metaLabel}>Contributing factors</Typography>
              <textarea
                placeholder="List any contributing factors"
                rows="3"
                value={rootCause.contributingFactors}
                onChange={handleRootCauseFieldChange("contributingFactors")}
                className={classes.input}
                style={{ resize: "vertical" }}
              />
            </Box>
            <Box style={{ display: "flex", justifyContent: "flex-end" }}>
              <AppButton
                label="Save"
                buttonColor={AppColor.mainColor}
                style={{ fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
                onClick={handleSaveRootCause}
              />
            </Box>
          </Box>
        </Box>

        <Box className={classes.card}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                <i className="fas fa-tasks" style={{ color: "#1D9E75" }}></i> Corrective actions
              </Typography>
              <Chip
                size="small"
                label={STEP_STATUS_LABEL[stepStatuses.CORRECTIVE_ACTION]}
                className={getStepBadgeClass(stepStatuses.CORRECTIVE_ACTION)}
                style={{ fontSize: "11px", fontWeight: 600 }}
              />
            </Box>
            <AppButton
              label="Add action"
              buttonColor={AppColor.mainColor}
              style={{ fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
              onClick={() => setIsModalOpen(true)}
            />
          </Box>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th} style={{ width: "40%" }}>Action</th>
                <th className={classes.th} style={{ width: "20%" }}>Due date</th>
                <th className={classes.th} style={{ width: "20%" }}>Status</th>
                <th className={classes.th} style={{ width: "20%", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(actions || []).length === 0 ? (
                <tr>
                  <td
                    className={classes.td}
                    colSpan={4}
                    style={{ textAlign: "center", color: "#888780", padding: "20px 0" }}
                  >
                    No data
                  </td>
                </tr>
              ) : (
                actions.map((item, index) => (
                  <tr key={item.id || index}>
                    <td className={classes.td}>{item.actionDescription || item.action}</td>
                    <td className={classes.td} style={{ color: "#5f5e5a" }}>
                      {formatDate(item.dueDate)}
                    </td>
                    <td className={classes.td}>
                      <Chip
                        size="small"
                        label={ACTION_STATUS_LABEL[item.status] || item.status}
                        className={getActionStatusClass(item.status)}
                        style={{ fontSize: "10px" }}
                      />
                    </td>
                    <td className={classes.td}>
                      <Box style={{ display: "flex", alignItems: "center", gap: "16px", justifyContent: "flex-end" }}>
                        {item.fileUrl && (
                          <>
                            <i
                              className="fas fa-eye"
                              title="View"
                              style={{ fontSize: "14px", color: "#1D9E75", cursor: "pointer" }}
                              onClick={() => {
                                setPreviewFileUrl(item.fileUrl);
                                setPreviewOpen(true);
                              }}
                            ></i>
                            <a
                              href={item.fileUrl}
                              download={item.fileName}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: "none", color: "inherit", display: "inline-flex" }}
                            >
                              <i
                                className="fas fa-download"
                                title="Download"
                                style={{ fontSize: "14px", color: "#1D9E75", cursor: "pointer" }}
                              ></i>
                            </a>
                          </>
                        )}
                        <i
                          className="fas fa-edit"
                          title="Edit"
                          style={{ fontSize: "14px", color: "#888780", cursor: "pointer" }}
                          onClick={() => handleOpenEditAction(item)}
                        ></i>
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>

        <Box className={classes.card}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              <i className="fas fa-paperclip" style={{ color: "#1D9E75" }}></i> Evidence and attachments
            </Typography>
            <Chip
              size="small"
              label={STEP_STATUS_LABEL[stepStatuses.EVIDENCE]}
              className={getStepBadgeClass(stepStatuses.EVIDENCE)}
              style={{ fontSize: "11px", fontWeight: 600 }}
            />
          </Box>
          <Box
            className={classes.uploadZone}
            style={{ cursor: isUploadingEvidence ? "default" : "pointer" }}
            onClick={() => !isUploadingEvidence && document.getElementById("investigation-evidence-input").click()}
          >
            {isUploadingEvidence ? (
              <>
                <CircularProgress size={24} style={{ color: "#1D9E75" }} />
                <Typography className={classes.uploadText} style={{ marginTop: 8 }}>
                  Uploading...
                </Typography>
              </>
            ) : (
              <>
                <i className={`fas fa-upload ${classes.uploadIcon}`}></i>
                <Typography className={classes.uploadText}>
                  Drag and drop files here or <span style={{ color: "#1D9E75", cursor: "pointer" }}>browse</span>
                </Typography>
                <Typography className={classes.uploadSubtext}>
                  Supports JPG, PNG, PDF, DOCX — max 20MB each
                </Typography>
              </>
            )}
            <input
              id="investigation-evidence-input"
              type="file"
              multiple
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png,.pdf,.docx"
              onChange={(e) => {
                handleEvidenceUpload(e.target.files);
                e.target.value = null;
              }}
              disabled={isUploadingEvidence}
            />
          </Box>
          {(evidenceList || []).length > 0 && (
            <Box style={{ marginTop: 12 }}>
              {evidenceList.map((evidence) => (
                <Box
                  key={evidence.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 13,
                    padding: "6px 0",
                    borderBottom: "0.5px solid #F3F4F6",
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {evidence.fileName || `Evidence #${evidence.id}`}
                  </span>
                  <Box style={{ display: "flex", alignItems: "center", gap: "20px", flexShrink: 0, marginLeft: 12 }}>
                    <i
                      className="fas fa-eye"
                      title="View"
                      style={{ fontSize: "13px", color: "#1D9E75", cursor: "pointer" }}
                      onClick={() => {
                        setPreviewFileUrl(evidence.fileUrl);
                        setPreviewOpen(true);
                      }}
                    />
                    <i
                      className="fas fa-download"
                      title="Download"
                      style={{ fontSize: "13px", color: "#1D9E75", cursor: "pointer" }}
                      onClick={() => handleDownloadEvidence(evidence)}
                    />
                    <i
                      className="fas fa-trash"
                      title="Delete"
                      style={{ fontSize: "13px", color: "#A32D2D", cursor: "pointer" }}
                      onClick={() => handleDeleteEvidence(evidence)}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box className={classes.card}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              <i className="fas fa-pen-fancy" style={{ color: "#1D9E75" }}></i> Director sign-off
            </Typography>
            <Chip
              size="small"
              label={STEP_STATUS_LABEL[stepStatuses.DIRECTOR_SIGN_OFF]}
              className={getStepBadgeClass(stepStatuses.DIRECTOR_SIGN_OFF)}
              style={{ fontSize: "11px", fontWeight: 600 }}
            />
          </Box>
          <Box
            style={{
              background: "#F5F5F3",
              borderRadius: "8px",
              border: "0.5px solid #e5e5e2",
              padding: "16px",
            }}
          >
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "14px",
                marginBottom: "14px",
              }}
            >
              <Box>
                <Typography className={classes.metaLabel}>Director name</Typography>
                <input
                  type="text"
                  placeholder="Director name"
                  value={director.name}
                  onChange={handleDirectorFieldChange("name")}
                  className={classes.input}
                />
              </Box>
              <Box>
                <Typography className={classes.metaLabel}>Sign-off date</Typography>
                <CustomDatePicker
                  field={{
                    name: "signOffDate",
                    value: director.signOffDate,
                    onChange: handleDirectorFieldChange("signOffDate"),
                  }}
                  form={{ errors: {}, touched: {} }}
                  placeholder="yyyy/MM/dd"
                />
              </Box>
            </Box>
            <Box style={{ marginBottom: "14px" }}>
              <Typography className={classes.metaLabel}>Director notes</Typography>
              <textarea
                placeholder="Final notes, outcome summary, or closeout remarks..."
                rows="3"
                value={director.notes}
                onChange={handleDirectorFieldChange("notes")}
                className={classes.input}
                style={{ resize: "none" }}
              />
            </Box>
            <Box style={{ display: "flex", justifyContent: "flex-end" }}>
              <AppButton
                label="Save"
                buttonColor={AppColor.mainColor}
                style={{ fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
                onClick={handleSaveDirectorSignOff}
              />
            </Box>
          </Box>
        </Box>

        <InvestigationActionForm
          open={isModalOpen}
          companyUserOptions={companyUserOptions}
          workerOptions={workerOptions}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleAddAction}
        />

        <InvestigationActionForm
          open={openEditActionDialog}
          isEdit
          initialAction={editingAction?.actionDescription}
          initialExpireDate={editingAction?.dueDate ? formatActionDueDate(editingAction.dueDate) : ""}
          initialOwner={editingAction?.owner}
          initialAssignedToUserId={editingAction?.assignedToUserId}
          initialFileName={editingAction?.fileName}
          initialStatus={editingAction?.status}
          companyUserOptions={companyUserOptions}
          workerOptions={workerOptions}
          onClose={() => setOpenEditActionDialog(false)}
          onConfirm={handleUpdateAction}
        />

        <PreviewDialog
          open={previewOpen}
          setOpen={setPreviewOpen}
          fileUrl={previewFileUrl}
        />

        <AppConfirmDialog
          open={!!deleteEvidenceTarget}
          onConfirm={confirmDeleteEvidence}
          onClose={() => setDeleteEvidenceTarget(null)}
          title="Delete Evidence"
          content={`Delete "${deleteEvidenceTarget ? (deleteEvidenceTarget.fileName || `Evidence #${deleteEvidenceTarget.id}`) : ""}"? This cannot be undone.`}
          type="danger"
        />

        <AppDialog
          open={stepDialog.open}
          title={`Update ${STEP_CONFIG.find((s) => s.key === stepDialog.step)?.label || ""} status`}
          onClose={() => setStepDialog({ open: false, step: null, status: null })}
          maxWidth="xs"
          actions={[
            {
              label: "Cancel",
              onPress: () => setStepDialog({ open: false, step: null, status: null }),
              mode: "outlined",
            },
            {
              label: "Confirm",
              onPress: handleConfirmStepStatus,
              buttonColor: AppColor.mainColor,
            },
          ]}
        >
          <Box>
            <Typography className={classes.metaLabel}>Status</Typography>
            <select
              className={classes.input}
              value={stepDialog.status || "OPEN"}
              onChange={(e) => setStepDialog((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="COMPLETE">Complete</option>
            </select>
          </Box>
        </AppDialog>
      </Box>
    </Box>
  );
};

export default InvestigationDetail;
